import { Tab, Tabs } from '@mui/material';
import { useEffect, useMemo, useState, type JSX } from 'react';
import { useLocation, useNavigate } from 'react-router';
import CasinoIcon from '../../assets/icons/navigation/CasinoIcon';
import LiveIcon from '../../assets/icons/navigation/LiveIcon';
import SportsIcon from '../../assets/icons/navigation/SportsIcon';
import useLocalization from '../../hooks/useLocalization';
import useProductAvailability from '../../hooks/useProductAvailability';

const TABS = [
  { label: 'sports', path: '/sports', icon: <SportsIcon /> },
  { label: 'mobileLive', path: '/in-play', icon: <LiveIcon /> },
  { label: 'casino', path: '/casino', icon: <CasinoIcon /> },
];

const styles = {
  tabs: {
    minHeight: 72,
    '& .MuiTab-root': {
      color: 'neutral.50',
      p: 1,
      textTransform: 'none',
      typography: {
        xs: 'subtitle3',
        md: 'h5',
      },
      display: 'flex',
      flexDirection: 'column',
      '&.Mui-selected': {
        color: 'info.main',
      },
    },
    '& .MuiSvgIcon-root': {
      fontSize: {
        xs: 24,
        md: 32,
      },
    },
    '& .MuiTabs-indicator': {
      display: 'none',
    },
  },
};

const getVisibleTabs = (hasSportsbookAccess: boolean, hasCasinoAccess: boolean) =>
  TABS.filter((tab) => {
    if (tab.path === '/casino') {
      return hasCasinoAccess;
    }

    return hasSportsbookAccess;
  });

const SidebarTabs = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { hasSportsbookAccess, hasCasinoAccess } = useProductAvailability();

  const tabs = useMemo(
    () => getVisibleTabs(hasSportsbookAccess, hasCasinoAccess),
    [hasCasinoAccess, hasSportsbookAccess]
  );
  const { getLocalizedConfig } = useLocalization();
  const localizedTabs = useMemo(() => getLocalizedConfig(tabs), [getLocalizedConfig, tabs]);

  const defaultValue = tabs.findIndex((tab) => location.pathname.startsWith(tab.path));
  const [value, setValue] = useState(defaultValue);

  useEffect(() => {
    setValue(tabs.findIndex((tab) => location.pathname.startsWith(tab.path)) ?? 0);
  }, [location.pathname, tabs]);

  const handleChange = (_: React.SyntheticEvent, newValue: number) => {
    setValue(newValue);
    navigate(tabs[newValue].path);
  };

  if (tabs.length === 0) {
    return null;
  }

  return (
    <Tabs value={value} onChange={handleChange} sx={styles.tabs} variant='fullWidth'>
      {localizedTabs.map(
        (
          tab: {
            label: string;
            path?: string;
            icon?: JSX.Element;
          },
          index: number
        ) => (
          <Tab
            key={tab.path}
            value={index}
            icon={tab.icon}
            iconPosition='top'
            label={tab.label}
            onClick={(e) => handleChange(e, index)}
          />
        )
      )}
    </Tabs>
  );
};

export default SidebarTabs;
